import { createContext, createProgram, createSourceTexture, deleteProgram, } from './gl-context.js';
import { curvedDrawPlan, curvedLayout } from './curve.js';
import { drawCurvedPlan } from './curve-draw.js';
import { cellContent, frameLayout, panelContent } from './fit.js';
import { faceDraws, foldedLayout, foldsToStage } from './folds.js';
import { apply, invert, rectToQuad, toColumnMajor, DegenerateQuadError, } from './homography.js';
import { byOrder, computeTransform, measure, quadToStage } from './layout.js';
import { emptyCellOutlines, longestHintText, placeHints, resolveCellHint } from './outlines.js';
import { applyTransform, createStage, renderMasks, renderOutlines, setBackground, } from './stage.js';
import { RENDERER_ERROR_CODES, } from './types.js';
import { cellKey, isCurved, isFolded, } from '@signa/shared';
import { UploadRegistry } from './upload-registry.js';
import { hasNewContent, naturalSizeOf, needsRealloc, nextTokenTrust, shouldUpload, trackPresentedFrames, TRUSTED_TOKEN, } from './source.js';
/**
 * キャンバスの実ピクセル倍率の上限(#174)。
 *
 * **塗りの費用は倍率の 2 乗で効く。** 画素ごとに行列演算と 2 分岐と標本化を
 * 行うので、DPR 2.0 の端末では 1.0 の **4 倍**の仕事になる。
 * 1.5 に抑えると **2.0 比で 44% 減**。
 *
 * **1.0 まで落とさない。** 盤面の輪郭が階段状に見え、
 * 「設置イメージの確認」という用途に差し支える。1.5 なら十分に鋭い。
 *
 * **頂点シェーダの `uDpr` と必ず同じ値を使う** ——
 * `clip = aPosition * uDpr / uResolution` なので、
 * **食い違うと盤面の位置と大きさが崩れる。**
 */
const MAX_RENDER_SCALE = 1.5;
/**
 * WebGL2 による描画([ADR-0007](../../../docs/adr/0007-projection-rendering-webgl.md))。
 *
 * PoC(D-13)で 8 枠 / 60fps 動画まで追加コストが測定限界以下だったのが採用理由。
 *
 * **公開 API を知らない。** V01 / V02 / S04 の 3 者が共有するため、
 * `RendererTemplate` は公開 API の形と独立に定義されている(設計書 7.4)。
 */
export class WebGlRenderer {
    fit;
    hint;
    container = null;
    stage = null;
    gl = null;
    program = null;
    template = null;
    options = null;
    transform = { width: 0, height: 0, offsetX: 0, offsetY: 0 };
    /**
     * セルと素材の台帳(#168 / [ADR-0031](../../../docs/adr/0031-source-keyed-upload.md))。
     *
     * **テクスチャは素材単位で 1 つ。** 同じ素材が複数のセルに載っていても
     * 転送は 1 回で済む。分割した盤面はセルごとに別の素材を持ちうる(#152)ので、
     * セル側の鍵は枠 ID だけでは足りない([`cellKey`](#cellKey))。
     *
     * **参照数の管理は [`UploadRegistry`](./upload-registry.ts) に置いてある** ——
     * WebGL は jsdom で試せない(ADR-0019)ため、GL を知らない形にして検査可能にした。
     */
    registry = new UploadRegistry({
        create: (source) => {
            // ここに来る時点で `gl` は非 null(`setSource` が確かめている)。
            const gl = this.gl;
            if (gl === null)
                throw new Error('WebGL コンテキストがありません');
            return {
                source,
                texture: createSourceTexture(gl),
                uploadedToken: undefined,
                // **負の値で始める。** 0 だと最初の描画フレームで「もう上げた」に
                // なって 1 枚目が出ない。
                uploadedFrame: -1,
                allocated: null,
                trust: TRUSTED_TOKEN,
                tracker: trackPresentedFrames(source),
            };
        },
        destroy: (entry) => {
            // **予約を外してから捨てる。** 残すと破棄した素材のコールバックが
            // 回り続ける(復号器も解放されない)。
            entry.tracker.stop();
            this.gl?.deleteTexture(entry.texture);
        },
    });
    highlighted = new Set();
    /**
     * **曲面の枠の描画計画の記憶**(#361)。枠 ID ごと。**形(`curve`)・分割(`grid`)・表示の寸法が同じ間は作り直さない** ——
     * 片は数百〜千を超え、毎フレーム割り付けと逆行列を作り直すと 1 フレームの予算を食う(分割 16 × 16 で十数 ms —— 最終レビューの実測)。
     * 形と分割は**参照**で照合する(テンプレートを差し替えると新しい値になる —— S04 の編集・ビューアの読み直し)。
     * GL の資源を持たないので、コンテキストを失っても捨てなくてよい。
     */
    curvedPlans = new Map();
    /**
     * 描画フレームの通し番号。**同じ素材を 1 フレームに 2 回上げない**ために使う。
     *
     * `requestVideoFrameCallback` が無い環境では提示フレームの番号が取れないので、
     * **この番号だけが重複を止める。**
     */
    frameCounter = 0;
    /**
     * 次のフレームを描く必要があるか(#174)。
     *
     * **何も変わっていないフレームは GL を一切触らない。** 30fps の素材を
     * 60Hz のループで描くと、**半分のフレームは前と同じ絵を描き直している** ——
     * 塗りの費用(盤面の面積 × DPR の 2 乗)とセルごとの描画呼び出しを丸ごと払う。
     *
     * **素材の新しさはここに含めない。** それは
     * [`hasNewContent`](./source.ts) が毎フレーム見る ——
     * 転送の判定と**同じ規則を共有**しないと、
     * **描かないまま中身が変わる**(= 画面が固まる)。
     *
     * **取りこぼすと画面が更新されなくなる**ので、変化の入口すべてで立てる:
     * `resize` / `setTemplate` / `setHighlight` / `setSource` / コンテキスト復帰。
     */
    dirty = true;
    /**
     * キャンバスの実ピクセル倍率。**`devicePixelRatio` に上限を掛けたもの**(#174)。
     *
     * `resize()` で決め、**頂点シェーダの `uDpr` にも同じ値を渡す。**
     */
    renderScale = 1;
    frameHandle = null;
    resizeObserver = null;
    contextLost = false;
    destroyed = false;
    constructor(fit = 'contain', 
    /**
     * **空のセルに出す案内**(#208)。**渡されたときだけ描く。**
     *
     * ビューア(V01 / V02)は渡し、**S04 は渡さない** —— 描画コアは 3 者共有
     * (設計書 7.4)で、S04 には編集ハンドルとレイヤーパネルがあるため
     * 発見可能性の問題がなく、出すと編集の邪魔になる。
     */
    hint) {
        this.fit = fit;
        this.hint = hint;
    }
    mount(container, template, opts) {
        if (this.destroyed)
            throw new Error('destroy 済みの Renderer は再利用できません');
        this.container = container;
        this.options = opts;
        this.template = template;
        this.stage = createStage(container);
        // **初期化の失敗はそのまま投げる。** フォールバックへの切り替えは
        // `createRenderer` が担う —— ここで受け止めると「WebGL 実装なのに
        // WebGL を使わずに動いている」状態ができ、追いにくくなる。
        this.gl = createContext(this.stage.canvas);
        this.program = createProgram(this.gl);
        this.stage.canvas.addEventListener('webglcontextlost', this.onContextLost);
        this.stage.canvas.addEventListener('webglcontextrestored', this.onContextRestored);
        // 親のサイズ変更に追従する。ウィンドウリサイズだけを見ると、
        // 埋め込み先のレイアウト変更(サイドバーの開閉など)を取りこぼす。
        this.resizeObserver = new ResizeObserver(() => {
            this.resize();
        });
        this.resizeObserver.observe(container);
        this.resize();
        this.startLoop();
    }
    setSource(frameId, cellIndex, source) {
        this.assignSource(frameId, cellIndex, source);
        /*
         * **割り当てが変わったら案内を描き直す**(#208)。
         *
         * 埋めたセルの枠は消え、外したセルには戻る。**`assignSource` は
         * 途中で戻る経路がいくつもある**ので、ここで包んで取りこぼしを防ぐ。
         */
        this.updateOutlines();
    }
    assignSource(frameId, cellIndex, source) {
        const key = cellKey({ frameId, cellIndex });
        this.dirty = true;
        if (source === null) {
            this.registry.detach(key);
            return;
        }
        /*
         * **ロスト中は作らない。** 復帰時に呼び出し側が再割り当てする
         * (下記 onContextRestored)。
         *
         * **`this.gl` の null だけでは足りない** —— ロスト中も `gl` は非 null の
         * ままで、`createTexture()` が null を返して**余計なエラー通知**が飛ぶ。
         */
        if (this.gl === null || this.contextLost)
            return;
        try {
            const result = this.registry.assign(key, source);
            /*
             * **同じ素材を同じセルに置き直したときは、転送だけやり直す。**
             *
             * 「差し替えたら転送し直す」は守らないといけない ——
             * 呼び出し側が要素の `src` を差し替えている可能性がある。
             * 一方でテクスチャを捨てて作り直すと、**共有している他のセルの絵が
             * 一瞬消える。**
             */
            if (result.kind === 'same') {
                result.entry.uploadedToken = undefined;
                result.entry.allocated = null;
            }
        }
        catch {
            this.reportError(RENDERER_ERROR_CODES.RENDER_FAILED, true);
        }
    }
    setTemplate(template) {
        this.template = template;
        // **消えた枠の計画を捨てる**(残る枠は照合で作り直すかが決まる —— 編集していない枠は作り直さない)。
        const ids = new Set(template.videoFrames.map((frame) => frame.id));
        for (const id of this.curvedPlans.keys()) {
            if (!ids.has(id))
                this.curvedPlans.delete(id);
        }
        this.dirty = true;
        // 解像度が変わればステージのサイズも変わる。
        this.resize();
    }
    setHighlight(ids) {
        this.highlighted = new Set(ids ?? []);
        this.dirty = true;
    }
    maxSourceSize() {
        // ロスト中・未初期化は 0(判定しない)。
        const value = this.gl?.getParameter(this.gl.MAX_TEXTURE_SIZE);
        return typeof value === 'number' ? value : 0;
    }
    resize() {
        if (this.stage === null || this.container === null || this.template === null)
            return;
        this.transform = computeTransform(measure(this.container), this.template.canvas, this.fit);
        applyTransform(this.stage, this.transform);
        this.dirty = true;
        // バッキングストアは DPR を掛けた実ピクセルで持つ。掛けないと
        // 高 DPI 端末で輪郭がぼやける。
        // **上限を掛けた倍率を 1 か所で決める**(#174)。`uDpr` にも同じ値を渡す。
        this.renderScale = Math.min(globalThis.devicePixelRatio || 1, MAX_RENDER_SCALE);
        this.stage.canvas.width = Math.max(1, Math.round(this.transform.width * this.renderScale));
        this.stage.canvas.height = Math.max(1, Math.round(this.transform.height * this.renderScale));
        setBackground(this.stage, this.template.backgroundUrl);
        renderMasks(this.stage, this.template.obstacles, this.transform, this.template.backgroundUrl, this.fit);
        // **マスクの後に呼ぶ。** `renderMasks` はステージ内の SVG を消してから
        // 組み直すので、先に描くと輪郭が巻き添えで消える。
        this.updateOutlines();
        this.options?.onTransform?.(this.transform);
    }
    /**
     * **素材が入っていないセルの輪郭を描き直す**(#208)。
     *
     * **`emptyCellHint` を渡されたときだけ描く。** 描画コアは V01 / V02 / S04 の
     * 3 者が共有しており(設計書 7.4)、**S04 では出さない** ——
     * 編集ハンドルとレイヤーパネルがあるので発見可能性の問題がなく、
     * 出すと編集の邪魔になる。
     *
     * **毎フレームではない。** 呼ぶのは「割り当てが変わったとき」と
     * 「レイアウトが変わったとき」だけ —— 再生中の負荷にしない。
     */
    updateOutlines() {
        const hint = this.hint;
        if (hint === undefined || this.stage === null || this.template === null)
            return;
        /*
         * **枠ごとに文言を解決する**(#240 / [ADR-0038](../../../docs/adr/0038-frame-source-kind-hint.md))。
         * 素材の種別で案内が変わるので、**文言の寸法も枠ごと**になる ——
         * #208 の時点では全体に 1 回でよかった。
         */
        const resolved = new Map(this.template.videoFrames.map((frame) => [frame.id, resolveCellHint(hint, frame.sourceKind)]));
        /*
         * **引けなかったら種類を言わない文言に落とす。** 形の上では起こらない
         * (同じ `videoFrames` から作った表と輪郭なので ID は必ず一致する)が、
         * **取り違えたときに案内が丸ごと消える**より、#208 の案内が出るほうが安全側。
         */
        const hintOf = (frameId) => resolved.get(frameId) ?? resolveCellHint(hint, null);
        const shapes = emptyCellOutlines(this.template.videoFrames, this.transform, (frameId, cellIndex) => this.hasSource(frameId, cellIndex));
        /*
         * **収まらないセルにも文言を置く**(#261)。重なる分は出さず、写真の端では
         * 内側へ寄せる —— 判断は純関数(`placeHints`)が持つ。
         *
         * 寸法は**長いほうの文言**(`longestHintText`)で見積もる。入力方式によって出る文言が
         * 変わる(CSS が切り替える)ので、**どちらが出ても同じ位置・同じ間引き**にする。
         */
        const anchors = placeHints(shapes, (shape) => longestHintText(hintOf(shape.frameId)), this.transform);
        renderOutlines(this.stage, shapes.map((shape, index) => {
            const at = anchors[index] ?? null;
            return {
                id: shape.frameId,
                points: shape.points,
                // 内向きの曲面の手前の裏に隠れる部分を消す(#361)。
                occluders: shape.occluders,
                // **`null` は文言を付けない**(他の文言と重なる —— 枠だけになる)。
                ...(at === null ? {} : { label: { at, hint: hintOf(shape.frameId) } }),
            };
        }), this.transform, this.highlighted);
    }
    /** そのセルに素材が載っているか。**台帳が知っている**(#168 の `UploadRegistry`)。 */
    hasSource(frameId, cellIndex) {
        return this.registry.sourceOf(cellKey({ frameId, cellIndex })) !== undefined;
    }
    destroy() {
        this.destroyed = true;
        this.stopLoop();
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.stage?.canvas.removeEventListener('webglcontextlost', this.onContextLost);
        this.stage?.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);
        this.registry.clear();
        this.curvedPlans.clear();
        if (this.program !== null)
            deleteProgram(this.program);
        this.program = null;
        // **コンテキストを明示的に失わせる。** ブラウザごとに同時数の上限があり、
        // GC を待つと SPA の画面遷移で上限に達して描画不能になる。
        this.gl?.getExtension('WEBGL_lose_context')?.loseContext();
        this.gl = null;
        this.stage?.root.remove();
        this.stage = null;
        this.container = null;
        this.template = null;
        this.options = null;
    }
    // -------------------------------------------------------------------------
    // 描画ループ
    // -------------------------------------------------------------------------
    /**
     * 描画を止める / 再開する(#233)。
     *
     * **書き出しのあいだ表示を止めるために使う。** 止めた分の GPU と CPU が
     * 書き出しへ回る —— 書き出しは素材を再生しながら撮るので、
     * **表示も同時に回っていると競合する。**
     *
     * **再開したら必ず描き直す。** `preserveDrawingBuffer: false` なので
     * **止めているあいだに canvas の中身が失われている**可能性があり、
     * `dirty` を立てないと**次に変化が起きるまで空白のまま**になる。
     */
    setPaused(paused) {
        if (this.destroyed)
            return;
        if (paused) {
            this.stopLoop();
            return;
        }
        // **コンテキストが失われている間は再開しない**(復帰処理が改めて始める)。
        if (this.contextLost)
            return;
        this.dirty = true;
        this.startLoop();
    }
    startLoop() {
        if (this.frameHandle !== null)
            return;
        const tick = () => {
            this.frameHandle = null;
            this.draw();
            if (!this.destroyed)
                this.frameHandle = requestAnimationFrame(tick);
        };
        this.frameHandle = requestAnimationFrame(tick);
    }
    stopLoop() {
        if (this.frameHandle === null)
            return;
        cancelAnimationFrame(this.frameHandle);
        this.frameHandle = null;
    }
    draw() {
        const { gl, program, template } = this;
        if (gl === null || program === null || template === null)
            return;
        if (this.contextLost || this.transform.width <= 0)
            return;
        /*
         * **変化が無いフレームは GL を一切触らない**(#174)。
         *
         * 30fps の素材を 60Hz のループで描くと、**半分のフレームは前と同じ絵を
         * 描き直している** —— 塗り(盤面の面積 × 倍率の 2 乗)とセルごとの
         * 描画呼び出しを丸ごと払っていた。
         *
         * **素材の新しさは転送の判定と同じ関数で見る**([`hasNewContent`](./source.ts))——
         * 別の規則にすると、**描かないまま中身が変わって画面が固まる。**
         */
        if (!this.dirty && !this.hasFreshSource())
            return;
        this.dirty = false;
        // **描画フレームの通し番号を進める。** 同じ素材を 1 フレームに
        // 2 回上げないための唯一の手掛かり(#168)。
        this.frameCounter++;
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.useProgram(program.program);
        gl.bindVertexArray(program.vao);
        gl.uniform2f(program.uniforms.resolution, gl.drawingBufferWidth, gl.drawingBufferHeight);
        // **キャンバスの倍率と同じ値**(#174)。食い違うと盤面の位置が崩れる。
        gl.uniform1f(program.uniforms.dpr, this.renderScale);
        gl.uniform1i(program.uniforms.texture, 0);
        // **order が小さいほど下**(F-10)。後に描いたものが上に載る。
        for (const frame of byOrder(template.videoFrames)) {
            // **曲面の枠は短冊ごとに描く**(#361 / ADR-0060)。判定は `isCurved` だけ。折れ目と両方を持つ壊れたデータでは曲面を優先する。
            if (isCurved(frame)) {
                this.drawCurvedFrame(gl, program, frame.id, frame.curve, frame.grid, frame.fit);
            }
            else if (isFolded(frame)) {
                // **折れた枠は面ごとに描く**(#339 / ADR-0057)。判定は `isFolded` だけ —— 境目が空なら平面。
                this.drawFoldedFrame(gl, program, frame.id, frame.quad, frame.grid, frame.folds, frame.fit);
            }
            else {
                this.drawFrame(gl, program, frame.id, frame.quad, frame.grid, frame.fit);
            }
        }
        gl.bindVertexArray(null);
    }
    /**
     * 枠を 1 つ描く。**分割していればセルごとに描く**(#152)。
     *
     * **ホモグラフィは枠に 1 つだけ作り、全セルで使い回す**
     * ([ADR-0029](../../../docs/adr/0029-panel-split-grid.md) の決定 3)。
     * セルごとに変換を作ると、隣り合うセルの境界が別々の計算を通って
     * **わずかにずれ、境目に隙間が出る。**
     */
    drawFrame(gl, program, frameId, quad, 
    /** **この枠の実比率と分割。** 未指定なら辺の長さの比に落ちる(#152)。 */
    grid, 
    /**
     * **この枠の収め方**(#326 / ADR-0056)。未指定は `contain`。
     *
     * **`frameFit` と呼び、省略可にしない。** このクラスには表示領域の収め方 `this.fit`
     * (`layout.ts` の `Fit`。埋め込みの `data-signa-fit`)があり、**型が同じ 2 値なので
     * 取り違えても型検査を通る。** 名前で区別し、渡し忘れは型エラーにする
     * (`compose.ts` と同じ形)—— WebGL の経路は単体テストで捕まえられない(ADR-0019)。
     */
    frameFit) {
        /*
         * **素材が 1 つも無ければ何もしない。**
         *
         * S04 は「素材を当てる前に形を整える」時間が長い。割り付けと
         * ホモグラフィを毎フレーム作るのは無駄で、退化したクアッドを
         * 通す機会も増える。
         */
        if (!this.hasAnySource(frameId))
            return;
        const stageQuad = quadToStage(quad, this.transform);
        /*
         * **写し元は盤面の矩形**(#147 / ADR-0028)。以前は動画の矩形をそのまま
         * クアッドへ写しており、縦横比が違うと歪んでいた。
         *
         * 分割は省略可。**未指定なら辺の長さの比のセル 1 つ**に落ちるので、
         * 入力が無くても引き伸ばしは止まる。
         */
        const layout = frameLayout(stageQuad, grid);
        // 潰れたクアッドでは描かない。
        if (layout === null)
            return;
        const { panel, cells } = layout;
        let toStage;
        let inverse;
        try {
            toStage = rectToQuad(panel.width, panel.height, stageQuad);
            /*
             * **`invert` も同じ try の中に置く。**
             *
             * 投げるのは `rectToQuad` だけではない —— 写す先が決まっても、
             * **行列が特異になれば `invert` が投げる**(`|det| < 1e-12`)。
             * 極端な比率(`9999999:0.000001`)でも起こる。
             *
             * ここを外に出すと例外が `draw()` を貫通し、`tick` の
             * `requestAnimationFrame` に到達しない —— **盤面全体が
             * リロードするまで更新されなくなる。**
             */
            inverse = invert(toStage);
        }
        catch (error) {
            // 頂点が潰れている間は描かない。**編集中に一時的に起こりうる**ので
            // エラー通知はしない(通知するとトーストが連射される)。
            if (error instanceof DegenerateQuadError)
                return;
            throw error;
        }
        // 盤面の座標系は全セルで共通。**枠ごとに 1 度だけ渡す。**
        gl.uniformMatrix3fv(program.uniforms.inverse, false, toColumnMajor(inverse));
        gl.uniform2f(program.uniforms.panelSize, panel.width, panel.height);
        gl.uniform1f(program.uniforms.highlight, this.highlighted.has(frameId) ? 1 : 0);
        for (const cell of cells) {
            this.drawCell(gl, program, frameId, cell, toStage, frameFit);
        }
    }
    /**
     * 画面に出していない中身を持つ素材が 1 つでもあるか(#174)。
     *
     * **転送の判定と同じ関数を使う。** 「描く必要があるか」と
     * 「転送する必要があるか」がずれると、**描かないまま中身が変わる。**
     */
    hasFreshSource() {
        for (const entry of this.registry.entries()) {
            if (hasNewContent({
                source: entry.source,
                currentToken: entry.tracker.token(),
                uploadedToken: entry.uploadedToken,
                trustToken: entry.trust.trustToken,
            })) {
                return true;
            }
        }
        return false;
    }
    /** この枠のセルに 1 つでも素材が当たっているか。 */
    hasAnySource(frameId) {
        return this.registry.hasAnyIn(frameId);
    }
    /**
     * セルを 1 つ描く。
     *
     * **素材の無いセルは描かない。** 黒く塗ると背景写真が隠れ、
     * 「まだ入れていない」のか「黒い素材が入っている」のかが見分けられない ——
     * 割り当てのないセルは、割り当てのない枠と同じく素通しにする。
     */
    drawCell(gl, program, frameId, cell, toStage, 
    /** 枠の収め方。`this.fit`(表示領域)と取り違えないよう名前を分ける —— `drawFrame` を参照。 */
    frameFit) {
        const entry = this.registry.entryOf(cellKey({ frameId, cellIndex: cell.index }));
        if (entry === undefined)
            return;
        // 準備が済む前は自然サイズが 0(メタデータ待ちの `<video>` /
        // 読み込み前の `<img>`)。テクスチャを作れないので描かない。
        const size = naturalSizeOf(entry.source);
        if (size.width === 0 || size.height === 0)
            return;
        /*
         * **収め方は枠の `fit` に従う**(#326 / ADR-0056)。`cover` では素材の矩形が
         * セルより大きくなるが、下で塗るのは**セルの 4 隅の範囲だけ**なので、
         * はみ出しは描かれずに切れる —— シェーダの黒帯の分岐にも入らない。
         */
        const content = cellContent(cell, size, frameFit);
        if (content === null)
            return;
        if (!this.prepareTexture(gl, entry, size))
            return;
        gl.uniform4f(program.uniforms.contentRect, content.x, content.y, content.width, content.height);
        /*
         * **セルの 4 隅を枠共通のホモグラフィで写す。** 隣り合うセルは盤面座標を
         * 厳密に共有している(`fit.ts` の `CellLayout.corners`)ので、
         * 写したあとの表示座標も厳密に一致する —— **境目に隙間が出ない。**
         *
         * 三角形は 2 つとも同じ 4 隅から作る。GPU の塗りつぶし規則により、
         * 辺を共有する三角形は境界の画素を過不足なく 1 度だけ塗る。
         */
        const [a, b, c, d] = cell.corners.map((corner) => apply(toStage, corner));
        if (a === undefined || b === undefined || c === undefined || d === undefined)
            return;
        this.drawQuad(gl, program, [a, b, c, d]);
    }
    /**
     * **折れた枠**を描く(#339 / [ADR-0057](../../../docs/adr/0057-folded-frame.md) の決定 2)。
     *
     * - 素材は**セル 0 の 1 本**(入れ物の寄せは `@signa/shared` の `linkedCells`)。**展開図全体**に収め
     *   ([`panelContent`](./fit.ts) —— 収め方は ADR-0056)、各面はその受け持ちを切り出す
     * - **面ごとにホモグラフィ**を作り、`uInverse` だけを替えて描く。`uContentRect` と `uPanelSize` は枠で共通
     * - 面の 4 隅は**割り付けの値そのもの**(隣の面と同じ値)—— 写像で求めないので、継ぎ目に割れが出ない
     * - **転送は枠に 1 回**(面ごとに繰り返さない —— #168 の「1 フレームに 2 回上げない」)
     * - 潰れた面は飛ばす([`faceDraws`](./folds.ts))
     */
    drawFoldedFrame(gl, program, frameId, quad, grid, folds, 
    /** 枠の収め方。`this.fit`(表示領域)と取り違えないよう名前を分ける —— `drawFrame` を参照。 */
    frameFit) {
        const entry = this.registry.entryOf(cellKey({ frameId, cellIndex: 0 }));
        if (entry === undefined)
            return;
        // 準備が済む前は自然サイズが 0。テクスチャを作れないので描かない。
        const size = naturalSizeOf(entry.source);
        if (size.width === 0 || size.height === 0)
            return;
        const layout = foldedLayout(quadToStage(quad, this.transform), grid, foldsToStage(folds, this.transform));
        if (layout === null)
            return;
        const faces = faceDraws(layout);
        if (faces.length === 0)
            return;
        const content = panelContent(layout.panel, size, frameFit);
        if (content === null)
            return;
        if (!this.prepareTexture(gl, entry, size))
            return;
        gl.uniform2f(program.uniforms.panelSize, layout.panel.width, layout.panel.height);
        gl.uniform1f(program.uniforms.highlight, this.highlighted.has(frameId) ? 1 : 0);
        gl.uniform4f(program.uniforms.contentRect, content.x, content.y, content.width, content.height);
        for (const face of faces) {
            gl.uniformMatrix3fv(program.uniforms.inverse, false, toColumnMajor(face.inverse));
            this.drawQuad(gl, program, face.corners);
        }
    }
    /**
     * **曲面の枠**を描く(#361 / [ADR-0060](../../../docs/adr/0060-curved-surface.md) の決定 4・5)。
     *
     * - **保存した形を写すだけ**(解かない)。展開図を短冊 × 行の片に分け、片ごとに `uInverse` だけを替えて描く
     *   ([`curvedLayout`](./curve.ts) / [`curvedDrawPlan`](./curve.ts))。**シェーダは変えない**
     * - **素材はセルごと**(分割・収め方は平面の枠と同じ —— 同意書の仮定 E)。転送は素材ごとに 1 回(`prepareTexture` が見る)
     * - **裏は描かない。** 内向きの曲面で手前の裏が奥を隠すところはステンシルで消す(GL の手順は [`curve-draw.ts`](./curve-draw.ts))
     * - 描けない形・潰れた片は描かない(エラーにしない —— 平面の枠の潰れたクアッドと同じ扱い)
     */
    drawCurvedFrame(gl, program, frameId, curve, grid, 
    /** 枠の収め方。`this.fit`(表示領域)と取り違えないよう名前を分ける —— `drawFrame` を参照。 */
    frameFit) {
        // **素材が 1 つも無ければ何もしない**(`drawFrame` と同じ —— 割り付けを毎フレーム作らない)。
        if (!this.hasAnySource(frameId))
            return;
        const plan = this.curvedPlanOf(frameId, curve, grid);
        if (plan === null)
            return;
        gl.uniform1f(program.uniforms.highlight, this.highlighted.has(frameId) ? 1 : 0);
        drawCurvedPlan(gl, program, plan, (cell) => {
            const entry = this.registry.entryOf(cellKey({ frameId, cellIndex: cell.index }));
            if (entry === undefined)
                return false;
            // 準備が済む前は自然サイズが 0。テクスチャを作れないので描かない。
            const size = naturalSizeOf(entry.source);
            if (size.width === 0 || size.height === 0)
                return false;
            const content = cellContent(cell, size, frameFit);
            if (content === null)
                return false;
            if (!this.prepareTexture(gl, entry, size))
                return false;
            gl.uniform4f(program.uniforms.contentRect, content.x, content.y, content.width, content.height);
            return true;
        });
    }
    /**
     * 曲面の枠の描画計画。**記憶([`curvedPlans`](#curvedPlans))と入力が同じなら作り直さない。**
     * 描けない形は `null`。
     */
    curvedPlanOf(frameId, curve, grid) {
        const { width, height } = this.transform;
        const memo = this.curvedPlans.get(frameId);
        if (memo?.curve === curve &&
            memo.grid === grid &&
            memo.width === width &&
            memo.height === height) {
            return memo.plan;
        }
        const layout = curvedLayout(curve, grid, this.transform);
        const plan = layout === null ? null : curvedDrawPlan(layout);
        this.curvedPlans.set(frameId, { curve, grid, width, height, plan });
        return plan;
    }
    /**
     * 4 隅(表示座標・左上から時計回り)の四角形を三角形 2 つで描く。
     *
     * 三角形は 2 つとも同じ 4 隅から作る。GPU の塗りつぶし規則により、
     * 辺を共有する三角形は境界の画素を過不足なく 1 度だけ塗る —— **隣と同じ値の頂点を渡せば隙間も重なりも出ない。**
     */
    drawQuad(gl, program, [a, b, c, d]) {
        gl.bindBuffer(gl.ARRAY_BUFFER, program.positionBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([...a, ...b, ...c, ...a, ...c, ...d]), gl.DYNAMIC_DRAW);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
    }
    /**
     * 素材のテクスチャを結び付け、**要るときだけ転送する**。描けないとき(確保に失敗した)は `false`。
     *
     * 平面の枠(セルごと)・折れた枠(枠に 1 回)・曲面の枠(セルごと —— #361)が共有する。
     */
    prepareTexture(gl, entry, size) {
        const { source, texture } = entry;
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        /*
         * **画像は 1 度だけ、動画は新しいフレームが来たときだけ転送する**
         * (#158 / #168。[ADR-0031](../../../docs/adr/0031-source-keyed-upload.md) が
         * [ADR-0030](../../../docs/adr/0030-source-kinds.md) の論点 3 を見直した)。
         *
         * **判断は純関数に置いてある**([`shouldUpload`](./source.ts))——
         * WebGL は jsdom で試せない(ADR-0019)ので、ここに条件を書くと検査できない。
         *
         * **1 度目は `texImage2D`、以後は `texSubImage2D`。** 前者は毎回テクスチャの
         * 形を作り直すが、後者は**既にある格納域に書き込むだけ**で済む。
         * 寸法が変わる経路が 2 つある(素材の差し替え / 動画のメタデータが後から届く)
         * ので、そのときだけ `texImage2D` に戻る。
         */
        // **1 度だけ読む。** 判断と記録で別の値を使うと、取り違えた分だけ
        // 転送を落としたり重ねたりする。
        const currentToken = entry.tracker.token();
        /*
         * **番号が壊れていないか先に見直す**(#168)。
         *
         * `requestVideoFrameCallback` は「無い」だけでなく**「あるが発火しなくなる」**
         * ことがある。番号が止まると転送も止まり、**動画は再生されているのに
         * キャンバスだけ固まる** —— 最も痛い壊れ方なので、番号が動かないまま
         * 描き続けたら毎フレーム転送に戻す。**動き出せば戻す**(双方向)。
         */
        entry.trust = nextTokenTrust({ ...entry, currentToken, staleSinceFrame: entry.trust.staleSinceFrame }, this.frameCounter);
        if (shouldUpload({ ...entry, currentToken, trustToken: entry.trust.trustToken }, this.frameCounter)) {
            if (needsRealloc(entry.allocated, size)) {
                /*
                 * **確保できたかを確かめてから記録する**(#170)。
                 *
                 * `texImage2D` は失敗しうる —— **中身の無い動画**(`readyState < 2`)や
                 * 上限を超えた寸法で `INVALID_VALUE` になり、**テクスチャは確保されない。**
                 * それを確保できたものとして記録すると、以後の転送は `texSubImage2D`
                 * になり、**level 0 が無いので `INVALID_OPERATION` で永久に書けない** ——
                 * **動画が真っ暗になる。**
                 *
                 * 中身の有無は [`shouldUpload`](./source.ts) が先に見ているが、
                 * **ここでも確かめる。** 理由を問わず「確保できていないのに
                 * `texSubImage2D` に進む」を止めたい —— 黒い盤面は原因が見えない。
                 *
                 * **`getError` はこの経路だけで呼ぶ。** 素材ごとに 1 回(と寸法が
                 * 変わった回)だけなので、GPU との同期の費用は無視できる。
                 * 毎フレーム呼ぶと描画そのものを詰まらせる。
                 */
                gl.getError(); // 前の誤りを自分のものと取り違えないよう掃除する
                gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
                if (gl.getError() !== gl.NO_ERROR)
                    return false; // 確保できていない。次のフレームで再挑戦する
                entry.allocated = { width: size.width, height: size.height };
            }
            else {
                gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, source);
            }
            entry.uploadedToken = currentToken;
            entry.uploadedFrame = this.frameCounter;
        }
        return true;
    }
    // -------------------------------------------------------------------------
    // コンテキストロスト(PoC 申し送り 2 — 必須)
    // -------------------------------------------------------------------------
    onContextLost = (event) => {
        // **preventDefault が必須。** 呼ばないと restored が発火せず、
        // 二度と復帰できない。
        event.preventDefault();
        this.contextLost = true;
        this.stopLoop();
        /*
         * テクスチャは失われている。**参照だけ捨てる**(要素は呼び出し側のもの)。
         *
         * **予約は外す。** `requestVideoFrameCallback` はコンテキストと無関係に
         * 回り続けるので、捨てた素材の分が残る(#168)。`clear` が `destroy` を
         * 呼ぶので、そこで外れる —— **失ったコンテキストへの `deleteTexture` は
         * 何もしない**ので安全。
         */
        this.registry.clear();
        this.options?.onContextLost?.();
    };
    onContextRestored = () => {
        this.contextLost = false;
        if (this.stage === null || this.destroyed)
            return;
        try {
            this.gl = createContext(this.stage.canvas);
            this.program = createProgram(this.gl);
        }
        catch {
            // 復帰に失敗したら諦める。**背景と枠は残る**ので黒画面にはならない。
            this.reportError(RENDERER_ERROR_CODES.RENDER_FAILED, false);
            return;
        }
        this.resize();
        this.startLoop();
        this.options?.onContextRestored?.();
        // **動画の再割り当ては呼び出し側の責務。** テクスチャは失われており、
        // `<video>` 要素を持っているのはビューア側だけ。
    };
    reportError(code, recoverable) {
        this.options?.onError({ code, recoverable });
    }
}
//# sourceMappingURL=webgl-renderer.js.map